/**
 * /dashboard/transactions — Transaction history.
 *
 * Banking-flavor list of every money + share movement on the account:
 * trades, payouts, deposits, withdrawals, referral credits, bonuses.
 * Mock data for now; structure mirrors the eventual GET
 * /v1/me/transactions response shape (1:1 swap when wired).
 */
"use client";

import { useState } from "react";
import {
  ArrowUpRight,
  ArrowDownRight,
  ArrowDownToLine,
  ArrowUpFromLine,
  Trophy,
  Gift,
  Heart,
  Star,
  type LucideIcon,
} from "lucide-react";
import { DashboardSubpageShell } from "@/components/v6/dashboard/DashboardSubpageShell";

type TxKind =
  | "trade_buy"
  | "trade_sell"
  | "resolution_payout"
  | "deposit"
  | "withdrawal"
  | "referral_bonus"
  | "monthly_reward"
  | "tip_sent"
  | "tip_received"
  | "creator_subscription";

// Note (founder direction 2026-05-24): copy-trader-related ledger
// movements (subscription fee, mirror trade settlement, performance
// rebate, etc.) are intentionally OMITTED from this page. They live
// on /dashboard/copytraders as a separate per-trader breakdown.

interface TxRow {
  id: string;
  kind: TxKind;
  title: string;
  detail: string;
  amountCents: number;
  positive: boolean;
  occurredAt: Date;
}

const KIND_META: Record<TxKind, { label: string; icon: LucideIcon }> = {
  trade_buy: { label: "Trade · Buy", icon: ArrowUpRight },
  trade_sell: { label: "Trade · Sell", icon: ArrowDownRight },
  resolution_payout: { label: "Resolution payout", icon: Trophy },
  deposit: { label: "Deposit", icon: ArrowDownToLine },
  withdrawal: { label: "Withdrawal", icon: ArrowUpFromLine },
  referral_bonus: { label: "Referral bonus", icon: Gift },
  monthly_reward: { label: "Monthly leaderboard", icon: Trophy },
  tip_sent: { label: "Tip sent", icon: Heart },
  tip_received: { label: "Tip received", icon: Heart },
  creator_subscription: { label: "Creator subscription", icon: Star },
};

const MOCK_TX: TxRow[] = [
  {
    id: "t1",
    kind: "trade_buy",
    title: "Brazil wins 2026 FIFA World Cup",
    detail: "Bought 30 YES @ 30¢",
    amountCents: -900,
    positive: false,
    occurredAt: new Date(Date.now() - 4 * 60_000),
  },
  {
    id: "t2",
    kind: "resolution_payout",
    title: "Will Bitcoin close 2026 above $200K?",
    detail: "Resolved YES — 50 shares paid out",
    amountCents: 2450,
    positive: true,
    occurredAt: new Date(Date.now() - 56 * 60_000),
  },
  {
    id: "t3",
    kind: "monthly_reward",
    title: "May 2026 leaderboard payout",
    detail: "Rank #4 · 50 USDC virtual",
    amountCents: 5000,
    positive: true,
    occurredAt: new Date(Date.now() - 3 * 60 * 60_000),
  },
  {
    id: "t4",
    kind: "trade_sell",
    title: "Will Lula win Brazil's 2026 election?",
    detail: "Sold 100 NO @ 48¢",
    amountCents: 4800,
    positive: true,
    occurredAt: new Date(Date.now() - 8 * 60 * 60_000),
  },
  {
    id: "t5",
    kind: "referral_bonus",
    title: "Referral · @friend123",
    detail: "First trade placed — bonus credited",
    amountCents: 1000,
    positive: true,
    occurredAt: new Date(Date.now() - 24 * 60 * 60_000),
  },
  {
    id: "t6",
    kind: "deposit",
    title: "Signup credit",
    detail: "Welcome bonus · $25",
    amountCents: 2500,
    positive: true,
    occurredAt: new Date(Date.now() - 5 * 24 * 60 * 60_000),
  },
  {
    id: "t7",
    kind: "tip_sent",
    title: "Tip · @marketwatch",
    detail: "Tipped on market analysis thread",
    amountCents: -200,
    positive: false,
    occurredAt: new Date(Date.now() - 2 * 24 * 60 * 60_000),
  },
  {
    id: "t8",
    kind: "creator_subscription",
    title: "Subscription · @crypto_skeptic",
    detail: "Monthly · Premium tier",
    amountCents: -500,
    positive: false,
    occurredAt: new Date(Date.now() - 7 * 24 * 60 * 60_000),
  },
  {
    id: "t9",
    kind: "tip_received",
    title: "Tip received from @whale_alpha",
    detail: "On Brazil 2026 market call",
    amountCents: 300,
    positive: true,
    occurredAt: new Date(Date.now() - 4 * 24 * 60 * 60_000),
  },
];

type Filter =
  | "all"
  | "trades"
  | "deposits"
  | "withdrawals"
  | "bonuses"
  | "rewards"
  | "tips"
  | "subscriptions";

const FILTER_LABELS: Record<Filter, string> = {
  all: "All",
  trades: "Trades",
  deposits: "Deposits",
  withdrawals: "Withdrawals",
  bonuses: "Bonuses",
  rewards: "Rewards",
  tips: "Tips",
  subscriptions: "Creator subscriptions",
};

function rowMatchesFilter(row: TxRow, f: Filter): boolean {
  if (f === "all") return true;
  if (f === "trades") return row.kind === "trade_buy" || row.kind === "trade_sell";
  if (f === "deposits") return row.kind === "deposit";
  if (f === "withdrawals") return row.kind === "withdrawal";
  // "Bonuses" = anything the platform credited to the user as an
  // incentive: referral payouts, signup credit, leaderboard prizes.
  // "Rewards" stays distinct = resolution payouts from markets the
  // user traded (their own earnings, not platform incentives).
  if (f === "bonuses")
    return (
      row.kind === "referral_bonus" || row.kind === "monthly_reward"
    );
  if (f === "rewards") return row.kind === "resolution_payout";
  if (f === "tips")
    return row.kind === "tip_sent" || row.kind === "tip_received";
  if (f === "subscriptions") return row.kind === "creator_subscription";
  return false;
}

export default function TransactionsPage() {
  const [filter, setFilter] = useState<Filter>("all");
  const rows = MOCK_TX.filter((r) => rowMatchesFilter(r, filter));

  return (
    <DashboardSubpageShell
      title="Transaction history"
      subtitle="Every money + share movement on your account."
    >
      {/* Filter — subtle select dropdown (founder direction 2026-05-24:
       * "i want them to subtle, inside of a select list"). Right-aligned
       * with a small "Show" label, so the page reads as a clean list
       * with chrome-tier filtering instead of a chip bar that competes
       * with the row content for attention. */}
      <div className="mb-4 flex items-center justify-end gap-2">
        <label
          htmlFor="tx-filter"
          className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: "var(--color-text3)" }}
        >
          Show
        </label>
        <select
          id="tx-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
          className="rounded-md border px-3 py-1.5 text-[12px] font-medium"
          style={{
            background: "var(--color-surface)",
            borderColor: "var(--color-border)",
            color: "var(--color-text)",
          }}
        >
          {(Object.keys(FILTER_LABELS) as Filter[]).map((f) => (
            <option key={f} value={f}>
              {FILTER_LABELS[f]}
            </option>
          ))}
        </select>
      </div>

      {/* Rows */}
      <ul
        className="overflow-hidden rounded-xl border"
        style={{
          background: "var(--color-surface)",
          borderColor: "var(--color-border)",
        }}
      >
        {rows.map((row, idx) => {
          const meta = KIND_META[row.kind];
          const Icon = meta.icon;
          return (
            <li
              key={row.id}
              className="flex items-center gap-3 px-4 py-3.5"
              style={{
                borderTop:
                  idx === 0 ? "none" : "1px solid var(--color-border)",
              }}
            >
              <span
                aria-hidden="true"
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
                style={{
                  background: row.positive
                    ? "color-mix(in srgb, var(--color-yes) 14%, transparent)"
                    : "color-mix(in srgb, var(--color-no) 14%, transparent)",
                  color: row.positive
                    ? "var(--color-yes)"
                    : "var(--color-no)",
                }}
              >
                <Icon size={16} strokeWidth={2} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">
                  {row.title}
                </div>
                <div
                  className="mt-0.5 truncate text-[11px]"
                  style={{ color: "var(--color-text3)" }}
                >
                  {meta.label} · {row.detail} · {formatRelative(row.occurredAt)}
                </div>
              </div>
              <span
                className="flex-shrink-0 font-mono text-[14px] font-bold tabular-nums"
                style={{
                  color: row.positive
                    ? "var(--color-yes)"
                    : "var(--color-no)",
                }}
              >
                {row.positive ? "+" : "−"}$
                {(Math.abs(row.amountCents) / 100).toFixed(2)}
              </span>
            </li>
          );
        })}
      </ul>

      {rows.length === 0 && (
        <div
          className="rounded-xl border px-5 py-10 text-center text-[14px]"
          style={{
            borderColor: "var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text2)",
          }}
        >
          No transactions match that filter.
        </div>
      )}
    </DashboardSubpageShell>
  );
}

function formatRelative(d: Date): string {
  const mins = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
